import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, Form, Input, InputNumber, Select, DatePicker, Upload, message, Divider, Space, Statistic, Row, Col, Tabs, Tag, Segmented, Grid, List, QRCode, Descriptions, Checkbox } from 'antd';
import { PlusOutlined, MinusCircleOutlined, EditOutlined, DeleteOutlined, UploadOutlined, DownloadOutlined, PrinterOutlined, SaveOutlined, CalculatorOutlined, BarChartOutlined, UserOutlined, SafetyCertificateOutlined, FileTextOutlined, ClockCircleOutlined } from '@ant-design/icons';
import api from '../../api/http';
import dayjs from 'dayjs';
import { useAuth } from '../../context/AuthContext';

const { Option } = Select;
const { TextArea } = Input;
const { TabPane } = Tabs;

const splitMeasuresToItems = (value) => {
  if (Array.isArray(value)) {
    return value.map(v => String(v || '').trim()).filter(Boolean);
  }
  if (!value) return [];
  const s = String(value);
  return s
    .split(/\r?\n|；|;|、/)
    .map(v => String(v || '').trim())
    .filter(Boolean);
};

const joinMeasureItems = (items) => {
  const arr = Array.isArray(items) ? items : [];
  return arr.map(v => String(v || '').trim()).filter(Boolean).join('\n');
};

const RiskClassificationPage = () => {
  const [form] = Form.useForm();
  const [riskLevels, setRiskLevels] = useState([]);
  const [evaluationCriteria, setEvaluationCriteria] = useState([]);
  const [hazardRecords, setHazardRecords] = useState([]);
  const [riskAdjustmentRecords, setRiskAdjustmentRecords] = useState([]);
  const [riskStatistics, setRiskStatistics] = useState({});
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalType, setModalType] = useState('create'); // create, edit, evaluate, treatment
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [users, setUsers] = useState([]);
  const [activeTab, setActiveTab] = useState('hazardRecords');
  const [riskTreatmentMeasures, setRiskTreatmentMeasures] = useState([]);
  const [riskResponsibilities, setRiskResponsibilities] = useState([]);
  const [riskRecordPhotoList, setRiskRecordPhotoList] = useState([]);
  const [riskRecordImageModalVisible, setRiskRecordImageModalVisible] = useState(false);
  const [riskRecordPreviewImage, setRiskRecordPreviewImage] = useState('');
  const [riskRecordDetailVisible, setRiskRecordDetailVisible] = useState(false);
  const [riskRecordDetail, setRiskRecordDetail] = useState(null);
  const [selectedRiskRecordIds, setSelectedRiskRecordIds] = useState([]);
  const auth = useAuth();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const getRiskRecordCreatedAtText = (record) => {
    const dt = record?.created_at || record?.created_time || record?.createdAt;
    if (!dt) return '-';
    return dayjs(dt).isValid() ? dayjs(dt).format('YYYY-MM-DD HH:mm') : String(dt);
  };

  const toggleRiskRecordSelect = (record, checked) => {
    const id = record?.id ?? record?.risk_record_id;
    if (id == null) return;
    setSelectedRiskRecordIds((prev) => {
      const set = new Set(prev);
      if (checked) set.add(id);
      else set.delete(id);
      return Array.from(set);
    });
  };

  const handlePrintSelectedRiskRecords = () => {
    const selected = (hazardRecords || []).filter(r => selectedRiskRecordIds.includes(r?.id ?? r?.risk_record_id));
    if (!selected.length) {
      message.warning('请先选择要打印的风险记录');
      return;
    }
    const blocks = selected.map((r, idx) => {
      const createdAtText = getRiskRecordCreatedAtText(r);
      const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${encodeURIComponent(buildRiskRecordQrValue(r))}`;
      return `
        <div class="record">
          <div class="record-title">隐患风险记录 ${idx + 1}</div>
          <div class="record-body">
            <table>
              <tbody>
                <tr>
                  <th>风险所在场所 / 部位</th>
                  <td colspan="3">${r?.risk_place_part || '-'}</td>
                </tr>
                <tr>
                  <th>风险描述</th>
                  <td colspan="3">${r?.risk_description || '-'}</td>
                </tr>
                <tr>
                  <th>风险等级</th>
                  <td>${r?.risk_level ?? '-'}</td>
                  <th>风险值</th>
                  <td>${r?.risk_value ?? '-'}</td>
                </tr>
                <tr>
                  <th>可能性等级</th>
                  <td>${r?.likelihood_level ?? '-'}</td>
                  <th>损失等级</th>
                  <td>${r?.loss_level ?? '-'}</td>
                </tr>
                <tr>
                  <th>检查周期</th>
                  <td>${r?.inspection_cycle || '-'}</td>
                  <th>责任人</th>
                  <td>${r?.inspection_responsible || '-'}</td>
                </tr>
                <tr>
                  <th>创建时间</th>
                  <td colspan="3">${createdAtText}</td>
                </tr>
                <tr>
                  <th>可能后果</th>
                  <td colspan="3">${r?.possible_consequence || '-'}</td>
                </tr>
              </tbody>
            </table>
            <div class="qr">
              <div class="qr-title">二维码</div>
              <img src="${qrUrl}" alt="二维码" />
            </div>
          </div>
        </div>`;
    }).join('');

    const html = `
      <html>
        <head>
          <title>隐患风险记录</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 16px; }
            .record { margin-bottom: 18px; page-break-inside: avoid; }
            .record-title { font-weight: 600; margin: 0 0 8px; }
            .record-body { display: flex; gap: 12px; align-items: flex-start; }
            table { width: 100%; border-collapse: collapse; font-size: 12px; }
            th, td { border: 1px solid #e5e5e5; padding: 6px 8px; text-align: left; vertical-align: top; }
            th { background: #fafafa; width: 160px; }
            .qr { width: 140px; text-align: center; border: 1px solid #e5e5e5; padding: 8px; }
            .qr-title { font-size: 12px; color: #666; margin-bottom: 6px; }
            .qr img { width: 120px; height: 120px; }
          </style>
        </head>
        <body>
          ${blocks}
        </body>
      </html>`;

    const win = window.open('', '_blank');
    if (!win) {
      message.error('无法打开打印窗口，请检查浏览器拦截设置');
      return;
    }
    win.document.open();
    win.document.write(html);
    win.document.close();
    setTimeout(() => {
      win.focus();
      win.print();
      win.close();
    }, 200);
  };


  const buildRiskRecordQrValue = (record) => {
    const id = record?.id ?? record?.risk_record_id ?? record?.record_id;
    return JSON.stringify({ type: 'risk_record', id });
  };

  const getRiskLevelTag = (level) => {
    const s = String(level || '').toUpperCase();
    if (!s) return <Tag>—</Tag>;
    if (s === 'HIGH' || s === '高') return <Tag color="red">高风险</Tag>;
    if (s === 'MEDIUM' || s === '中') return <Tag color="orange">中风险</Tag>;
    return <Tag color="green">低风险</Tag>;
  };

  const openRiskRecordDetail = (record) => {
    setRiskRecordDetail(record || null);
    setRiskRecordDetailVisible(true);
  };

  const closeRiskRecordDetail = () => {
    setRiskRecordDetailVisible(false);
    setRiskRecordDetail(null);
  };

  const renderRiskRecordCard = (record) => {
    const riskLevelLabel = record?.risk_level || '—';
    const riskShortText = String(riskLevelLabel || '').toUpperCase() === 'HIGH'
      ? '高'
      : String(riskLevelLabel || '').toUpperCase() === 'MEDIUM'
        ? '中'
        : String(riskLevelLabel || '').toUpperCase() === 'LOW'
          ? '低'
          : (String(riskLevelLabel || '').slice(0, 1) || '');
    const bookmarkColor = String(riskLevelLabel || '').toUpperCase() === 'HIGH'
      ? '#ff4d4f'
      : String(riskLevelLabel || '').toUpperCase() === 'MEDIUM'
        ? '#faad14'
        : '#52c41a';
    const createdAtText = getRiskRecordCreatedAtText(record);
    const recordId = record?.id ?? record?.risk_record_id;
    const isSelected = selectedRiskRecordIds.includes(recordId);

    return (
        <div
          onDoubleClick={() => openRiskRecordDetail(record)}
          style={{
            position: 'relative',
            border: '1px solid rgba(0,0,0,0.06)',
            borderRadius: 8,
            padding: 10,
            paddingLeft: 32,
            paddingRight: 70,
            background: '#fff'
          }}
        >
        <div
          style={{
            position: 'absolute',
            top: 8,
            left: 8
          }}
        >
          <Checkbox
            checked={isSelected}
            onChange={(e) => toggleRiskRecordSelect(record, e.target.checked)}
          />
        </div>

        <div
          style={{
            position: 'absolute',
            top: 8,
            right: 8,
            width: 52,
            height: 52,
            background: '#fff',
            borderRadius: 6,
            border: '1px solid rgba(0,0,0,0.06)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <QRCode value={buildRiskRecordQrValue(record)} size={40} bordered={false} />
        </div>

        <div
          style={{
            position: 'absolute',
            top: -1,
            right: 68,
            width: 28,
            height: 40,
            background: bookmarkColor,
            clipPath: 'polygon(0 0, 100% 0, 100% 100%, 50% 78%, 0 100%)',
            borderTopLeftRadius: 4,
            borderTopRightRadius: 4,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            paddingTop: 6
          }}
        >
          <div style={{ color: '#fff', fontSize: 12, fontWeight: 600, lineHeight: '14px', textAlign: 'center', width: 20 }}>
            {riskShortText}
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontWeight: 600, marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {record?.risk_place_part || '-'}
            </div>
            <div style={{ color: 'rgba(0,0,0,0.65)', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
              {record?.risk_description || '-'}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8, gap: 10 }}>
          <div style={{ color: 'rgba(0,0,0,0.65)', minWidth: 0, fontSize: 12 }}>
            <ClockCircleOutlined style={{ marginRight: 4 }} />
            {createdAtText}
          </div>
          {getRiskLevelTag(record?.risk_level)}
        </div>
      </div>
    );
  };

  const buildInspectionCycleText = (values) => {
    const t = String(values?.inspection_cycle_type || '').trim();
    if (t === 'daily') return '每日';
    if (t === 'weekly') return '每周';
    if (t === 'monthly') return '每月';
    if (t === 'every_n_days') {
      const n = Number(values?.inspection_cycle_n_days);
      if (Number.isFinite(n) && n > 0) return `每${n}天`;
      return '';
    }
    // 兼容旧值：直接透传文本
    return String(values?.inspection_cycle || '').trim();
  };

  const parseInspectionCycleToFields = (cycleText) => {
    const s = String(cycleText || '').trim();
    if (!s) return { inspection_cycle_type: 'daily', inspection_cycle_n_days: null };
    if (s === '每日') return { inspection_cycle_type: 'daily', inspection_cycle_n_days: null };
    if (s === '每周') return { inspection_cycle_type: 'weekly', inspection_cycle_n_days: null };
    if (s === '每月') return { inspection_cycle_type: 'monthly', inspection_cycle_n_days: null };
    const m = s.match(/^每(\d+)天$/);
    if (m) return { inspection_cycle_type: 'every_n_days', inspection_cycle_n_days: Number(m[1]) };
    return { inspection_cycle_type: 'daily', inspection_cycle_n_days: null };
  };

  const normalizeRiskRecordPhotosToFileList = (photos) => {
    const arr = Array.isArray(photos) ? photos : [];
    return arr
      .map((p, idx) => {
        const objectName = p?.objectName || p?.object_name || '';
        const url = p?.url || '';
        const name = p?.fileName || p?.file_name || `照片${idx + 1}`;
        if (!objectName && !url) return null;
        return {
          uid: objectName || `${idx}`,
          name,
          status: 'done',
          url,
          response: {
            objectName,
            url,
            fileName: name
          }
        };
      })
      .filter(Boolean);
  };

  const buildRiskRecordPhotosPayload = (fileList) => {
    const arr = Array.isArray(fileList) ? fileList : [];
    return arr
      .map(f => {
        const r = f?.response?.photo || f?.response || {};
        const objectName = r?.objectName || r?.object_name || f?.objectName || '';
        const fileName = r?.fileName || r?.file_name || f?.name || '';
        return objectName ? { objectName, fileName } : null;
      })
      .filter(Boolean);
  };

  const getRiskRecordPhotoObjectName = (fileItem) => {
    const r = fileItem?.response?.photo || fileItem?.response || {};
    return r?.objectName || r?.object_name || fileItem?.objectName || '';
  };

  const uploadRiskRecordPhotoToRecord = async (recordId, rawFile) => {
    const formData = new FormData();
    formData.append('file', rawFile);
    const resp = await api.post(
      `/risk-management/hazard-records/${recordId}/photos`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return resp?.data;
  };

  const customUploadRiskRecordPhoto = async ({ file, onSuccess, onError }) => {
    try {
      // 创建态允许先选图；真正上传在“保存成功后（拿到 id）”自动执行
      if (!selectedRecord?.id) {
        onSuccess?.({ pending: true }, file);
        return;
      }
      const formData = new FormData();
      formData.append('file', file);
      const resp = await api.post(
        `/risk-management/hazard-records/${selectedRecord.id}/photos`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      onSuccess?.(resp?.data || {}, file);
    } catch (e) {
      onError?.(e);
    }
  };

  const computeMatrixRisk = (likelihoodLevel, lossLevel) => {
    const l = Number.parseInt(String(likelihoodLevel ?? ''), 10);
    const s = Number.parseInt(String(lossLevel ?? ''), 10);
    if (!Number.isFinite(l) || !Number.isFinite(s)) return { risk_value: null, risk_level: null, risk_level_label: '' };
    if (l < 1 || l > 5 || s < 1 || s > 5) return { risk_value: null, risk_level: null, risk_level_label: '' };
    const risk_value = l * s;
    let risk_level = 'LOW';
    let risk_level_label = '低风险';
    if (risk_value >= 16) {
      risk_level = 'HIGH';
      risk_level_label = '高风险';
    } else if (risk_value >= 6) {
      risk_level = 'MEDIUM';
      risk_level_label = '中风险';
    }
    return { risk_value, risk_level, risk_level_label };
  };

  // 风险等级颜色：红/橙/黄/蓝（用于名称与标签展示）
  const getRiskTagColor = (level) => {
    const v = String(level || '').toUpperCase();
    if (v === 'HIGH' || v === '高') return 'red';
    if (v === 'MEDIUM' || v === '中') return 'orange';
    if (v === 'LOW' || v === '低') return 'gold';
    return 'blue';
  };

  // 将“控制措施”解析为预设措施ID数组（兼容：后端可能返回数组/对象/文本）
  const parseControlMeasuresToIds = (value) => {
    if (!value) return [];

    // 1) 已经是数组：优先直接提取 id
    if (Array.isArray(value)) {
      const ids = value
        .map(v => {
          if (v == null) return null;
          if (typeof v === 'number') return v;
          if (typeof v === 'string' && /^\d+$/.test(v.trim())) return Number(v.trim());
          if (typeof v === 'object' && v.id != null && /^\d+$/.test(String(v.id))) return Number(v.id);
          return null;
        })
        .filter(v => Number.isFinite(v));
      return Array.from(new Set(ids));
    }

    // 2) 对象：可能形如 {items:[...]} 或单条 {id:...}
    if (typeof value === 'object') {
      if (Array.isArray(value.items)) return parseControlMeasuresToIds(value.items);
      if (value.id != null && /^\d+$/.test(String(value.id))) return [Number(value.id)];
      // 兜底：尝试当成字符串处理
    }

    // 3) 文本：按名称去匹配预设措施（兼容旧存储形态）
    const text = String(value);
    const parts = text
      .split(/；|;|\n/)
      .map(s => s.trim())
      .filter(Boolean);
    const ids = [];
    for (const p of parts) {
      const name = p.split('：')[0].trim();
      const found = (riskTreatmentMeasures || []).find(m => String(m.name || '').trim() === name);
      if (found?.id) ids.push(found.id);
    }
    return Array.from(new Set(ids));
  };

  // 初始化数据
  useEffect(() => {
    fetchRiskLevels();
    fetchEvaluationCriteria();
    fetchHazardRecords();
    fetchRiskAdjustmentRecords();
    fetchRiskStatistics();
    fetchUsers();
    fetchRiskTreatmentMeasures();
    fetchRiskResponsibilities();
  }, []);

  // 小工具：兼容后端既可能返回数组，也可能返回 {success, data}
  const unwrapArray = (resp) => {
    const d = resp?.data;
    if (Array.isArray(d)) return d;
    if (d && Array.isArray(d.data)) return d.data;
    return [];
  };
  const unwrapObject = (resp) => {
    const d = resp?.data;
    if (d && d.data && typeof d.data === 'object') return d.data;
    return d || {};
  };

  // 规范化风险等级数据形状，兼容 /levels 与 /risk-levels 返回
  const normalizeRiskLevels = (arr) => {
    return (arr || []).map(l => {
      const score = l.score_range ?? ((l.min_score !== undefined && l.max_score !== undefined) ? { min: l.min_score, max: l.max_score } : undefined);
      const name = l.name ?? l.level_name ?? l.level_code ?? '';
      const treatment = l.treatment_measures ?? l.color;
      return { ...l, name, score_range: score, treatment_measures: treatment };
    });
  };

  // 获取风险等级列表
  const fetchRiskLevels = async () => {
    try {
      const response = await api.get('/risk-management/levels');
      setRiskLevels(normalizeRiskLevels(unwrapArray(response)));
    } catch (error) {
      message.error('获取风险等级列表失败');
    }
  };

  // 获取评估标准列表
  const fetchEvaluationCriteria = async () => {
    try {
      const response = await api.get('/risk-management/criteria');
      setEvaluationCriteria(unwrapArray(response));
    } catch (error) {
      message.error('获取评估标准列表失败');
    }
  };

  // 获取隐患记录列表
  const fetchHazardRecords = async () => {
    try {
      const response = await api.get('/risk-management/hazard-records');
      setHazardRecords(unwrapArray(response));
    } catch (error) {
      message.error('获取隐患记录列表失败');
    }
  };

  // 获取风险调整记录列表
  const fetchRiskAdjustmentRecords = async () => {
    try {
      const response = await api.get('/risk-management/adjustment-records');
      setRiskAdjustmentRecords(unwrapArray(response));
    } catch (error) {
      message.error('获取风险调整记录列表失败');
    }
  };

  // 获取风险统计数据
  const fetchRiskStatistics = async () => {
    try {
      const response = await api.get('/risk-management/statistics');
      setRiskStatistics(unwrapObject(response));
    } catch (error) {
      message.error('获取风险统计数据失败');
    }
  };

  // 获取用户列表
  const fetchUsers = async () => {
    try {
      const response = await api.get('/hazard/users');
      setUsers(response.data.data);
    } catch (error) {
      console.error('获取用户列表失败:', error);
      // 忽略用户列表获取失败，继续渲染页面
    }
  };

  // 获取风险管控措施列表
  const fetchRiskTreatmentMeasures = async () => {
    try {
      const response = await api.get('/risk-management/treatment-measures');
      setRiskTreatmentMeasures(unwrapArray(response));
    } catch (error) {
      message.error('获取风险管控措施列表失败');
    }
  };

  // 获取风险责任人列表
  const fetchRiskResponsibilities = async () => {
    try {
      const response = await api.get('/risk-management/responsibilities');
      setRiskResponsibilities(unwrapArray(response));
    } catch (error) {
      message.error('获取风险责任人列表失败');
    }
  };

  // 显示模态框
  const showModal = (type, record = null) => {
    setModalType(type);
    setSelectedRecord(record);
    setRiskRecordPhotoList([]);
    if (record) {
      if (type === 'editHazardRecord') {
        const computed = computeMatrixRisk(record.likelihood_level, record.loss_level);
        const cycleFields = parseInspectionCycleToFields(record.inspection_cycle);
        form.setFieldsValue({
          risk_place_part: record.risk_place_part,
          risk_description: record.risk_description,
          possible_consequence: record.possible_consequence,
          likelihood_level: record.likelihood_level ?? null,
          loss_level: record.loss_level ?? null,
          risk_value: record.risk_value ?? computed.risk_value,
          risk_level: record.risk_level ?? computed.risk_level,
          control_measures: parseControlMeasuresToIds(record.control_measures),
          inspection_cycle: record.inspection_cycle || '',
          ...cycleFields,
          inspection_responsible: record.inspection_responsible || ''
        });
        setRiskRecordPhotoList(normalizeRiskRecordPhotosToFileList(record.risk_record_photos));
      } else if (type === 'editTreatmentMeasure') {
        form.setFieldsValue({
          name: record.name,
          risk_level: record.risk_level,
          description: record.description || ''
        });
      } else if (type === 'edit') {
        form.setFieldsValue({
          ...record,
          treatment_measures: splitMeasuresToItems(record.treatment_measures)
        });
      } else {
        form.setFieldsValue(record);
      }
    } else {
      form.resetFields();
      if (type === 'create') {
        form.setFieldsValue({ treatment_measures: [''] });
      } else if (type === 'treatment') {
        form.setFieldsValue({
          items: [{ name: '', risk_level: 'LOW', description: '' }]
        });
      } else if (type === 'createHazardRecord') {
        setRiskRecordPhotoList([]);
        form.setFieldsValue({
          inspection_cycle_type: 'daily',
          inspection_cycle_n_days: null
        });
      }
    }
    setIsModalVisible(true);
  };

  // 关闭模态框
  const handleCancel = () => {
    setIsModalVisible(false);
    setSelectedRecord(null);
    setRiskRecordPhotoList([]);
    setRiskRecordImageModalVisible(false);
    setRiskRecordPreviewImage('');
  };

  // 编辑弹窗：刷新照片 URL（presigned URL 可能会过期）
  useEffect(() => {
    if (!isModalVisible) return;
    if (modalType !== 'editHazardRecord') return;
    if (!selectedRecord?.id) return;

    let cancelled = false;
    (async () => {
      // 直接用 /files/preview 逐个刷新图片 URL，避免部分环境缺少单条接口导致 404
      const photos = Array.isArray(selectedRecord?.risk_record_photos) ? selectedRecord.risk_record_photos : [];
      const normalized = photos
        .map((p) => ({
          objectName: p?.objectName || p?.object_name || '',
          fileName: p?.fileName || p?.file_name || '',
          url: p?.url || ''
        }))
        .filter(p => p.objectName);

      if (normalized.length === 0) return;

      const refreshed = await Promise.all(normalized.map(async (p) => {
        try {
          const r = await api.get('/files/preview', { params: { objectName: p.objectName } });
          const url = r?.data?.url || '';
          return { ...p, url: url || p.url };
        } catch (_) {
          return p;
        }
      }));

      if (cancelled) return;
      setRiskRecordPhotoList(normalizeRiskRecordPhotosToFileList(refreshed));
    })();

    return () => { cancelled = true; };
  }, [isModalVisible, modalType, selectedRecord?.id, selectedRecord?.risk_record_photos]);

  // 提交表单
  const handleSubmit = async (values) => {
    try {
      let response;
      if (modalType === 'create') {
        response = await api.post('/risk-management/levels', {
          ...values,
          treatment_measures: joinMeasureItems(values.treatment_measures)
        });
        message.success('风险等级创建成功');
        fetchRiskLevels();
      } else if (modalType === 'edit') {
        response = await api.put(`/risk-management/levels/${selectedRecord.id}`, {
          ...values,
          treatment_measures: joinMeasureItems(values.treatment_measures)
        });
        message.success('风险等级编辑成功');
        fetchRiskLevels();
      } else if (modalType === 'evaluate') {
        response = await api.post(`/risk-management/evaluate-risk/${selectedRecord.id}`, values);
        message.success('风险评估完成');
        fetchHazardRecords();
        fetchRiskAdjustmentRecords();
        fetchRiskStatistics();
      } else if (modalType === 'treatment') {
        const items = Array.isArray(values?.items) ? values.items : [values];
        const normalized = items
          .map(i => ({
            name: String(i?.name || '').trim(),
            risk_level: i?.risk_level,
            description: i?.description
          }))
          .filter(i => i.name || i.risk_level || (i.description && String(i.description).trim()));

        if (normalized.length === 0) {
          message.warning('请至少填写一条管控措施');
          return;
        }

        const failed = [];
        for (const item of normalized) {
          try {
            // eslint-disable-next-line no-await-in-loop
            await api.post(`/risk-management/treatment-measures`, item);
          } catch (e) {
            const reason = e?.response?.data?.error || e?.message || '提交失败';
            failed.push({ name: item?.name || '未命名', reason });
          }
        }

        if (failed.length > 0) {
          message.warning(`已提交 ${normalized.length - failed.length} 条，失败 ${failed.length} 条（例如：${failed[0].name} - ${failed[0].reason}）`);
        } else {
          message.success(`管控措施配置成功（共 ${normalized.length} 条）`);
        }
        fetchRiskTreatmentMeasures();
      } else if (modalType === 'editTreatmentMeasure') {
        response = await api.put(`/risk-management/treatment-measures/${selectedRecord.id}`, values);
        message.success('管控措施更新成功');
        fetchRiskTreatmentMeasures();
      } else if (modalType === 'createHazardRecord') {
        const computed = computeMatrixRisk(values.likelihood_level, values.loss_level);
        const inspectionCycleText = buildInspectionCycleText(values);
        if (!inspectionCycleText) {
          message.warning('请完善检查周期');
          return;
        }
        const cmIds = Array.isArray(values.control_measures) ? values.control_measures : [];
        const cmText = (cmIds || [])
          .map(id => (riskTreatmentMeasures || []).find(m => m.id === id))
          .filter(Boolean)
          .map(m => `${m.name}${m.description ? `：${m.description}` : ''}`)
          .join('；');
        response = await api.post('/risk-management/hazard-records', {
          ...values,
          inspection_cycle: inspectionCycleText,
          risk_value: computed.risk_value,
          risk_level: computed.risk_level,
          control_measures: cmText,
          risk_record_photos: buildRiskRecordPhotosPayload(riskRecordPhotoList)
        });
        const newId = response?.data?.id;
        // 若用户在创建态先选了图片（还没上传），这里拿到 id 后自动上传到 MinIO，并落库 risk_record_photos
        const currentList = Array.isArray(riskRecordPhotoList) ? riskRecordPhotoList : [];
        const pending = currentList
          .map((f, idx) => ({ f, idx }))
          .filter(({ f }) => !!f?.originFileObj && !getRiskRecordPhotoObjectName(f));

        if (newId && pending.length > 0) {
          message.loading({ key: 'riskRecordPhotoUpload', content: `正在上传照片（${pending.length}张）...`, duration: 0 });
          const failed = [];
          for (const item of pending) {
            try {
              // eslint-disable-next-line no-await-in-loop
              const data = await uploadRiskRecordPhotoToRecord(newId, item.f.originFileObj);
              const photo = data?.photo;
              if (photo?.objectName && photo?.url) {
                currentList[item.idx] = {
                  ...item.f,
                  status: 'done',
                  url: photo.url,
                  response: data
                };
              }
            } catch (e) {
              failed.push(item.f?.name || '未命名');
            }
          }
          setRiskRecordPhotoList([...(currentList || [])]);
          if (failed.length > 0) {
            message.warning({ key: 'riskRecordPhotoUpload', content: `照片上传完成，失败 ${failed.length} 张（例如：${failed[0]}）` });
          } else {
            message.success({ key: 'riskRecordPhotoUpload', content: '照片上传成功' });
          }
        }

        message.success('隐患风险记录录入成功');
        fetchHazardRecords();
        fetchRiskStatistics();
      } else if (modalType === 'editHazardRecord') {
        const computed = computeMatrixRisk(values.likelihood_level, values.loss_level);
        const inspectionCycleText = buildInspectionCycleText(values);
        if (!inspectionCycleText) {
          message.warning('请完善检查周期');
          return;
        }
        const cmIds = Array.isArray(values.control_measures) ? values.control_measures : [];
        const cmText = (cmIds || [])
          .map(id => (riskTreatmentMeasures || []).find(m => m.id === id))
          .filter(Boolean)
          .map(m => `${m.name}${m.description ? `：${m.description}` : ''}`)
          .join('；');
        response = await api.put(`/risk-management/hazard-records/${selectedRecord.id}`, {
          ...values,
          inspection_cycle: inspectionCycleText,
          risk_value: computed.risk_value,
          risk_level: computed.risk_level,
          control_measures: cmText,
          risk_record_photos: buildRiskRecordPhotosPayload(riskRecordPhotoList)
        });
        message.success('隐患风险记录更新成功');
        fetchHazardRecords();
        fetchRiskStatistics();
      }
      setIsModalVisible(false);
    } catch (error) {
      message.error(error?.response?.data?.error || '操作失败，请重试');
    }
  };

  const handleDeleteTreatmentMeasure = async (record) => {
    Modal.confirm({
      title: '确认删除该管控措施？',
      content: '删除后不可恢复。',
      okText: '删除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          await api.delete(`/risk-management/treatment-measures/${record.id}`);
          message.success('删除成功');
          fetchRiskTreatmentMeasures();
        } catch (e) {
          message.error(e?.response?.data?.error || '删除失败，请重试');
        }
      }
    });
  };

  // 删除风险等级
  const handleDelete = async (id) => {
    try {
      await api.delete(`/risk-management/levels/${id}`);
      message.success('风险等级删除成功');
      fetchRiskLevels();
    } catch (error) {
      message.error('删除失败，请重试');
    }
  };

  // 自动评估风险
  const handleAutoEvaluate = async (record) => {
    try {
      const response = await api.post(`/risk-management/auto-evaluate/${record.id}`);
      message.success('自动风险评估完成');
      fetchHazardRecords();
      fetchRiskAdjustmentRecords();
      fetchRiskStatistics();
    } catch (error) {
      message.error(error?.response?.data?.error || '自动评估失败，请重试');
    }
  };

  // 删除手工录入的风险记录
  const handleDeleteHazardRecord = async (record) => {
    Modal.confirm({
      title: '确认删除该风险记录？',
      content: '删除后不可恢复。',
      okText: '删除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          await api.delete(`/risk-management/hazard-records/${record.id}`);
          message.success('删除成功');
          fetchHazardRecords();
          fetchRiskStatistics();
        } catch (e) {
          message.error('删除失败，请重试');
        }
      }
    });
  };

  // 分配风险责任人
  const assignRiskResponsibility = async (record) => {
    try {
      const response = await api.post(`/risk-management/assign-responsibility`, record);
      message.success('风险责任人分配成功');
      fetchRiskResponsibilities();
    } catch (error) {
      message.error('分配失败，请重试');
    }
  };

  // 风险等级表格列
  const riskLevelColumns = [
    {
      title: '风险等级名称',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Tag color={getRiskTagColor(record.level_code || text)}>{text}</Tag>
      )
    },
    {
      title: '风险等级描述',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: '风险分值范围',
      dataIndex: 'score_range',
      key: 'score_range',
      render: (text) => {
        const min = text?.min;
        const max = text?.max;
        return `${min ?? '-'} - ${max ?? '-'}`;
      },
    },
    {
      title: '处理措施',
      dataIndex: 'treatment_measures',
      key: 'treatment_measures',
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text) => dayjs(text).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space size="middle">
          <Button icon={<EditOutlined />} size="small" onClick={() => showModal('edit', record)}>编辑</Button>
          <Button icon={<DeleteOutlined />} size="small" danger onClick={() => handleDelete(record.id)}>删除</Button>
        </Space>
      ),
    },
  ];

  // 评估标准表格列
  const evaluationCriteriaColumns = [
    {
      title: '评估指标名称',
      dataIndex: 'criteria_name',
      key: 'criteria_name',
    },
    {
      title: '评估指标代码',
      dataIndex: 'criteria_code',
      key: 'criteria_code',
    },
    {
      title: '评估指标类别',
      dataIndex: 'category',
      key: 'category',
    },
    {
      title: '权重',
      dataIndex: 'weight',
      key: 'weight',
    },
    {
      title: '评估指标描述',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: '状态',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (text) => (
        <Tag color={text ? 'green' : 'red'}>{text ? '启用' : '禁用'}</Tag>
      )
    },
  ];

  // 隐患记录表格列
  const hazardRecordColumns = [
    {
      title: '风险所在场所 / 部位',
      dataIndex: 'risk_place_part',
      key: 'risk_place_part',
      width: 160,
      render: (text) => text || '-',
    },
    {
      title: '风险描述',
      dataIndex: 'risk_description',
      key: 'risk_description',
      width: 220,
      ellipsis: true,
    },
    {
      title: '风险可能导致的后果',
      dataIndex: 'possible_consequence',
      key: 'possible_consequence',
      width: 220,
      ellipsis: true,
    },
    {
      title: '可能性(1-5)',
      dataIndex: 'likelihood_level',
      key: 'likelihood_level',
      width: 110,
      render: (v) => (v ? `${v}级` : '-'),
    },
    {
      title: '损失(1-5)',
      dataIndex: 'loss_level',
      key: 'loss_level',
      width: 100,
      render: (v) => (v ? `${v}级` : '-'),
    },
    {
      title: '风险值',
      dataIndex: 'risk_value',
      key: 'risk_value',
      width: 90,
      render: (v) => (v !== null && v !== undefined && v !== '' ? v : '-'),
    },
    {
      title: '风险等级',
      dataIndex: 'risk_level',
      key: 'risk_level',
      width: 110,
      render: (text) => {
        return <Tag color={getRiskTagColor(text)}>{text}</Tag>;
      },
    },
    {
      title: '控制措施',
      dataIndex: 'control_measures',
      key: 'control_measures',
      width: 260,
      ellipsis: true,
    },
    {
      title: '操作',
      key: 'action',
      width: 320,
      fixed: 'right',
      render: (_, record) => (
        <Space size="middle">
          <Button icon={<CalculatorOutlined />} size="small" onClick={() => handleAutoEvaluate(record)}>自动评估</Button>
          <Button icon={<EditOutlined />} size="small" onClick={() => showModal('evaluate', record)}>手动调整</Button>
          {record?.source === 'manual_entry' && (
            <>
              <Button icon={<EditOutlined />} size="small" onClick={() => showModal('editHazardRecord', record)}>编辑</Button>
              <Button icon={<DeleteOutlined />} size="small" danger onClick={() => handleDeleteHazardRecord(record)}>删除</Button>
            </>
          )}
        </Space>
      ),
    },
  ];

  // 风险调整记录表格列
  const adjustmentRecordColumns = [
    {
      title: '记录ID',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: '隐患ID',
      dataIndex: 'hazard_id',
      key: 'hazard_id',
    },
    {
      title: '原风险等级',
      dataIndex: 'old_risk_level',
      key: 'old_risk_level',
      render: (text) => {
        let color = '';
        if (text === '低' || text === 'LOW') color = 'green';
        if (text === '中' || text === 'MEDIUM') color = 'orange';
        if (text === '高' || text === 'HIGH') color = 'red';
        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: '新风险等级',
      dataIndex: 'new_risk_level',
      key: 'new_risk_level',
      render: (text) => {
        let color = '';
        if (text === '低' || text === 'LOW') color = 'green';
        if (text === '中' || text === 'MEDIUM') color = 'orange';
        if (text === '高' || text === 'HIGH') color = 'red';
        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: '原风险分值',
      dataIndex: 'old_risk_score',
      key: 'old_risk_score',
    },
    {
      title: '新风险分值',
      dataIndex: 'new_risk_score',
      key: 'new_risk_score',
    },
    {
      title: '调整原因',
      dataIndex: 'reason',
      key: 'reason',
    },
    {
      title: '调整人',
      dataIndex: 'adjusted_by',
      key: 'adjusted_by',
    },
    {
      title: '调整时间',
      dataIndex: 'adjusted_at',
      key: 'adjusted_at',
      render: (text) => dayjs(text).format('YYYY-MM-DD HH:mm:ss'),
    },
  ];

  // 管控措施表格列
  const treatmentMeasuresColumns = [
    {
      title: '措施名称',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '适用风险等级',
      dataIndex: 'risk_level',
      key: 'risk_level',
      render: (text) => {
        let color = '';
        if (text === '低' || text === 'LOW') color = 'green';
        if (text === '中' || text === 'MEDIUM') color = 'orange';
        if (text === '高' || text === 'HIGH') color = 'red';
        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: '措施描述',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text) => dayjs(text).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: '操作',
      key: 'actions',
      width: 160,
      render: (_, record) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => showModal('editTreatmentMeasure', record)}>
            编辑
          </Button>
          <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDeleteTreatmentMeasure(record)}>
            删除
          </Button>
        </Space>
      )
    }
  ];

  // 风险责任人表格列
  const responsibilityColumns = [
    {
      title: '风险项目',
      dataIndex: 'risk_project',
      key: 'risk_project',
    },
    {
      title: '风险等级',
      dataIndex: 'risk_level',
      key: 'risk_level',
      render: (text) => {
        let color = '';
        if (text === '低' || text === 'LOW') color = 'green';
        if (text === '中' || text === 'MEDIUM') color = 'orange';
        if (text === '高' || text === 'HIGH') color = 'red';
        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: '责任人',
      dataIndex: 'responsible_person',
      key: 'responsible_person',
      render: (text) => <span><UserOutlined /> {text}</span>,
    },
    {
      title: '责任描述',
      dataIndex: 'responsibility_description',
      key: 'responsibility_description',
    },
    {
      title: '分配时间',
      dataIndex: 'assigned_at',
      key: 'assigned_at',
      render: (text) => dayjs(text).format('YYYY-MM-DD HH:mm:ss'),
    },
  ];



  return (
    <div>
      <h1 style={{ marginBottom: 24 }}>风险分级管理</h1>
      
      {/* 统计卡片 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={12} md={6}>
          <Card>
            <Statistic 
              title="低风险数量" 
              value={riskStatistics.low_count || 0} 
              prefix={<BarChartOutlined />} 
              valueStyle={{ color: '#52c41a' }} 
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} md={6}>
          <Card>
            <Statistic 
              title="中风险数量" 
              value={riskStatistics.medium_count || 0} 
              prefix={<BarChartOutlined />} 
              valueStyle={{ color: '#faad14' }} 
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} md={6}>
          <Card>
            <Statistic 
              title="高风险数量" 
              value={riskStatistics.high_count || 0} 
              prefix={<BarChartOutlined />} 
              valueStyle={{ color: '#ff4d4f' }} 
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} md={6}>
          <Card>
            <Statistic 
              title="总风险数量" 
              value={riskStatistics.total_count || 0} 
              prefix={<BarChartOutlined />} 
              valueStyle={{ color: '#1890ff' }} 
            />
          </Card>
        </Col>
      </Row>

      {/* 标签页 */}
      <div style={{ marginBottom: 16 }}>
        <Space direction={isMobile ? 'vertical' : 'horizontal'} wrap style={isMobile ? { width: '100%' } : undefined}>
          <Button 
            type={activeTab === 'hazardRecords' ? 'primary' : 'default'} 
            onClick={() => setActiveTab('hazardRecords')}
            icon={<FileTextOutlined />}
            block={isMobile}
          >
            隐患风险记录
          </Button>
          <Button 
            type={activeTab === 'riskLevels' ? 'primary' : 'default'} 
            onClick={() => setActiveTab('riskLevels')}
            icon={<SafetyCertificateOutlined />}
            block={isMobile}
          >
            风险等级管理
          </Button>
          <Button 
            type={activeTab === 'evaluationCriteria' ? 'primary' : 'default'} 
            onClick={() => setActiveTab('evaluationCriteria')}
            icon={<CalculatorOutlined />}
            block={isMobile}
          >
            风险评估模块
          </Button>
          <Button 
            type={activeTab === 'adjustmentRecords' ? 'primary' : 'default'} 
            onClick={() => setActiveTab('adjustmentRecords')}
            icon={<SaveOutlined />}
            block={isMobile}
          >
            风险调整记录
          </Button>
          <Button 
            type={activeTab === 'treatmentMeasures' ? 'primary' : 'default'} 
            onClick={() => setActiveTab('treatmentMeasures')}
            icon={<SafetyCertificateOutlined />}
            block={isMobile}
          >
            管控措施配置
          </Button>
          <Button 
            type={activeTab === 'responsibilities' ? 'primary' : 'default'} 
            onClick={() => setActiveTab('responsibilities')}
            icon={<UserOutlined />}
            block={isMobile}
          >
            责任人管理
          </Button>
        </Space>
      </div>

      {/* 风险等级管理 */}
      {activeTab === 'riskLevels' && (
        <Card title="风险等级管理" extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => showModal('create')} block={isMobile}>添加风险等级</Button>}>
          <Table columns={riskLevelColumns} dataSource={riskLevels} rowKey="id" size={isMobile ? 'small' : 'middle'} scroll={isMobile ? { x: 'max-content' } : undefined} />
        </Card>
      )}

      {/* 风险评估模块 */}
      {activeTab === 'evaluationCriteria' && (
        <Card title="风险评估模块">
          <Table columns={evaluationCriteriaColumns} dataSource={evaluationCriteria} rowKey="id" size={isMobile ? 'small' : 'middle'} scroll={isMobile ? { x: 'max-content' } : undefined} />
        </Card>
      )}

      {/* 隐患风险记录 */}
      {activeTab === 'hazardRecords' && (
        <Card
          title="隐患风险记录"
          extra={
            <Space direction={isMobile ? 'vertical' : 'horizontal'} wrap>
              <Button icon={<PrinterOutlined />} onClick={handlePrintSelectedRiskRecords} block={isMobile}>
                打印选中
              </Button>
              <Button type="primary" icon={<PlusOutlined />} onClick={() => showModal('createHazardRecord')} block={isMobile}>
                录入隐患风险记录
              </Button>
            </Space>
          }
        >
          <List
            dataSource={hazardRecords}
            grid={{ gutter: isMobile ? 12 : 16, xs: 1, sm: 1, md: 2, lg: 2, xl: 3, xxl: 3 }}
            pagination={{
              pageSize: 12,
              showSizeChanger: !isMobile,
              simple: isMobile,
              showTotal: (total) => `共 ${total} 条记录`
            }}
            renderItem={(item) => (
              <List.Item key={String(item?.id || item?.risk_record_id || Math.random())}>
                {renderRiskRecordCard(item)}
              </List.Item>
            )}
          />
        </Card>
      )}

      {/* 风险调整记录 */}
      {activeTab === 'adjustmentRecords' && (
        <Card title="风险调整记录">
          <Table columns={adjustmentRecordColumns} dataSource={riskAdjustmentRecords} rowKey="id" size={isMobile ? 'small' : 'middle'} scroll={isMobile ? { x: 'max-content' } : undefined} />
        </Card>
      )}

      {/* 管控措施配置 */}
      {activeTab === 'treatmentMeasures' && (
        <Card title="管控措施配置" extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => showModal('treatment')} block={isMobile}>添加管控措施</Button>}>
          <Table columns={treatmentMeasuresColumns} dataSource={riskTreatmentMeasures} rowKey="id" size={isMobile ? 'small' : 'middle'} scroll={isMobile ? { x: 'max-content' } : undefined} />
        </Card>
      )}

      {/* 责任人管理 */}
      {activeTab === 'responsibilities' && (
        <Card title="责任人管理">
          <Table columns={responsibilityColumns} dataSource={riskResponsibilities} rowKey="id" size={isMobile ? 'small' : 'middle'} scroll={isMobile ? { x: 'max-content' } : undefined} />
        </Card>
      )}

      {/* 模态框 */}
      <Modal
        title={
          modalType === 'create' ? '创建风险等级' : 
          modalType === 'edit' ? '编辑风险等级' : 
          modalType === 'evaluate' ? '评估风险等级' : 
          modalType === 'createHazardRecord' ? '录入隐患风险记录' :
          modalType === 'editHazardRecord' ? '编辑隐患风险记录' :
          modalType === 'editTreatmentMeasure' ? '编辑管控措施' :
          '配置管控措施'
        }
        open={isModalVisible}
        onCancel={handleCancel}
        footer={null}
        width={isMobile ? '100%' : 800}
        style={isMobile ? { top: 16 } : undefined}
      >
        <Form
          form={form}
          layout="vertical"
          onValuesChange={(changed, all) => {
            if (modalType !== 'createHazardRecord' && modalType !== 'editHazardRecord') return;
            if (!('likelihood_level' in (changed || {})) && !('loss_level' in (changed || {}))) return;
            const computed = computeMatrixRisk(all?.likelihood_level, all?.loss_level);
            const next = {};
            if (computed.risk_value !== form.getFieldValue('risk_value')) next.risk_value = computed.risk_value;
            if (computed.risk_level !== form.getFieldValue('risk_level')) next.risk_level = computed.risk_level;
            if (Object.keys(next).length) form.setFieldsValue(next);
          }}
          onFinish={handleSubmit}
        >
          {(modalType === 'createHazardRecord' || modalType === 'editHazardRecord') && (
            <>
              <Form.Item
                name="risk_place_part"
                label="风险所在场所 / 部位"
                rules={[{ required: true, message: '请输入风险所在场所/部位' }]}
              >
                <Input placeholder="例如：1号宿舍 3楼 走廊 / 车间A 配电箱" />
              </Form.Item>

              <Form.Item
                name="risk_description"
                label="风险描述"
                rules={[{ required: true, message: '请输入风险描述' }]}
              >
                <TextArea placeholder="请输入风险描述" rows={3} />
              </Form.Item>

              <Form.Item
                name="possible_consequence"
                label="风险可能导致的后果"
              >
                <TextArea placeholder="请输入可能导致的后果" rows={3} />
              </Form.Item>

              <Form.Item
                name="likelihood_level"
                label="可能性（1-5）"
                rules={[{ required: true, message: '请选择可能性等级(1-5)' }]}
              >
                <Select placeholder="请选择发生可能性等级">
                  <Option value={1}>1级（极低）：几乎不可能发生，近3年无同类案例</Option>
                  <Option value={2}>2级（较低）：发生概率低，仅特殊条件下出现</Option>
                  <Option value={3}>3级（中等）：偶尔发生，有明确诱发因素</Option>
                  <Option value={4}>4级（较高）：发生概率高，诱发因素常见</Option>
                  <Option value={5}>5级（极高）：极易发生，几乎必然出现</Option>
                </Select>
              </Form.Item>

              <Form.Item
                name="loss_level"
                label="损失（1-5）"
                rules={[{ required: true, message: '请选择损失等级(1-5)' }]}
              >
                <Select placeholder="请选择损失程度等级">
                  <Option value={1}>1级（轻微）：无人员伤害，财产损失＜500元</Option>
                  <Option value={2}>2级（较轻）：轻微人员不适或无伤害，500元≤损失＜2000元</Option>
                  <Option value={3}>3级（中等）：人员轻微受伤，2000元≤损失＜5000元</Option>
                  <Option value={4}>4级（严重）：人员重伤或多人轻伤，5000元≤损失＜20000元</Option>
                  <Option value={5}>5级（特别严重）：人员死亡或群伤，损失≥20000元</Option>
                </Select>
              </Form.Item>

              <Form.Item noStyle shouldUpdate={(prev, cur) => prev.likelihood_level !== cur.likelihood_level || prev.loss_level !== cur.loss_level}>
                {() => {
                  const l = form.getFieldValue('likelihood_level');
                  const s = form.getFieldValue('loss_level');
                  const computed = computeMatrixRisk(l, s);
                  return (
                    <Row gutter={16}>
                      <Col xs={24} md={12}>
                        <Form.Item label="风险值（自动计算）">
                          <Input value={computed.risk_value ?? ''} disabled placeholder="选择可能性与损失后自动计算" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item label="风险等级（自动生成）">
                          <Input value={computed.risk_level ? `${computed.risk_level_label}（${computed.risk_level}）` : ''} disabled placeholder="选择可能性与损失后自动生成" />
                        </Form.Item>
                      </Col>
                    </Row>
                  );
                }}
              </Form.Item>

              <Form.Item name="risk_value" hidden><Input /></Form.Item>
              <Form.Item name="risk_level" hidden><Input /></Form.Item>

              <Form.Item
                name="control_measures"
                label="控制措施"
              >
                <Select
                  mode="multiple"
                  allowClear
                  placeholder="请选择控制措施（来自预设表）"
                  optionFilterProp="label"
                  options={(riskTreatmentMeasures || [])
                    .filter(m => {
                      const currentLevel = form.getFieldValue('risk_level');
                      if (!currentLevel) return true;
                      return String(m.risk_level || '').toUpperCase() === String(currentLevel).toUpperCase();
                    })
                    .map(m => ({
                      value: m.id,
                      label: `${m.name}${m.description ? `：${m.description}` : ''}`
                    }))}
                />
              </Form.Item>

              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item label="检查周期" required>
                    <Space size={8} wrap>
                      <Form.Item
                        name="inspection_cycle_type"
                        noStyle
                        rules={[{ required: true, message: '请选择检查周期' }]}
                      >
                        <Segmented
                          options={[
                            { label: '每日', value: 'daily' },
                            { label: '每N天', value: 'every_n_days' },
                            { label: '每周', value: 'weekly' },
                            { label: '每月', value: 'monthly' }
                          ]}
                          onChange={(v) => {
                            if (v !== 'every_n_days') {
                              form.setFieldValue('inspection_cycle_n_days', null);
                            }
                          }}
                        />
                      </Form.Item>

                      <Form.Item
                        noStyle
                        shouldUpdate={(prev, cur) => prev.inspection_cycle_type !== cur.inspection_cycle_type}
                      >
                        {({ getFieldValue }) => {
                          const t = getFieldValue('inspection_cycle_type');
                          if (t !== 'every_n_days') return null;
                          return (
                            <Form.Item
                              name="inspection_cycle_n_days"
                              noStyle
                              rules={[{ required: true, message: '请输入天数' }]}
                            >
                              <InputNumber
                                min={1}
                                precision={0}
                                placeholder="天数"
                                style={{ width: 110 }}
                              />
                            </Form.Item>
                          );
                        }}
                      </Form.Item>
                    </Space>
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="inspection_responsible"
                    label="责任人"
                  >
                    <Select
                      showSearch
                      allowClear
                      placeholder="请选择责任人"
                      optionFilterProp="label"
                      options={(users || []).map(u => ({
                        value: u?.name,
                        label: `${u?.name || ''}${u?.username ? ` (${u.username})` : ''}`
                      }))}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item label="照片">
                <Upload
                  accept="image/*"
                  listType="picture-card"
                  fileList={riskRecordPhotoList}
                  disabled={modalType === 'createHazardRecord' || !selectedRecord?.id}
                  customRequest={customUploadRiskRecordPhoto}
                  onPreview={(file) => {
                    const url = file?.url || file?.thumbUrl;
                    if (!url) return;
                    setRiskRecordPreviewImage(url);
                    setRiskRecordImageModalVisible(true);
                  }}
                  onChange={({ fileList }) => {
                    const next = (fileList || []).map(f => {
                      const photo = f?.response?.photo || f?.response || {};
                      const url = f.url || photo.url || '';
                      const objectName = photo.objectName || photo.object_name || f.objectName || '';
                      return {
                        ...f,
                        url,
                        objectName
                      };
                    });
                    setRiskRecordPhotoList(next);
                  }}
                >
                  <div>
                    <UploadOutlined />
                    <div style={{ marginTop: 8 }}>上传</div>
                  </div>
                </Upload>
                {modalType === 'createHazardRecord' ? (
                  <div style={{ color: 'rgba(0,0,0,0.45)' }}>提示：请先保存记录，再进入编辑上传照片。</div>
                ) : null}
              </Form.Item>
            </>
          )}

          {modalType !== 'evaluate' &&
            modalType !== 'treatment' &&
            modalType !== 'editTreatmentMeasure' &&
            modalType !== 'createHazardRecord' &&
            modalType !== 'editHazardRecord' && (
            <>
              <Form.Item
                name="name"
                label="风险等级名称"
                rules={[{ required: true, message: '请输入风险等级名称' }]}
              >
                <Input placeholder="请输入风险等级名称" />
              </Form.Item>

              <Form.Item
                name="description"
                label="风险等级描述"
                rules={[{ required: true, message: '请输入风险等级描述' }]}
              >
                <TextArea placeholder="请输入风险等级描述" rows={3} />
              </Form.Item>

              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 8 }}>风险分值范围</label>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name={['score_range', 'min']}
                      noStyle
                      rules={[{ required: true, message: '请输入最小值' }]}
                    >
                      <Input placeholder="最小值" type="number" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name={['score_range', 'max']}
                      noStyle
                      rules={[{ required: true, message: '请输入最大值' }]}
                    >
                      <Input placeholder="最大值" type="number" />
                    </Form.Item>
                  </Col>
                </Row>
              </div>

              <Form.Item
                name="treatment_measures"
                label="处理措施"
                rules={[{
                  validator: (_, v) => {
                    const arr = Array.isArray(v) ? v : [];
                    const ok = arr.some(x => String(x || '').trim());
                    return ok ? Promise.resolve() : Promise.reject(new Error('请至少填写1条处理措施'));
                  }
                }]}
              >
                <Form.List name="treatment_measures">
                  {(fields, { add, remove }) => (
                    <div>
                      {fields.map((field, idx) => (
                        <Space key={field.key} style={{ display: 'flex', marginBottom: 8 }} align="start">
                          <Form.Item
                            {...field}
                            style={{ flex: 1, marginBottom: 0 }}
                            rules={[{ required: true, whitespace: true, message: '请输入处理措施' }]}
                          >
                            <Input placeholder={`处理措施${idx + 1}`} />
                          </Form.Item>
                          {fields.length > 1 ? (
                            <MinusCircleOutlined onClick={() => remove(field.name)} />
                          ) : null}
                        </Space>
                      ))}
                      <Button type="dashed" onClick={() => add('')} block icon={<PlusOutlined />}>
                        增加一条
                      </Button>
                    </div>
                  )}
                </Form.List>
              </Form.Item>
            </>
          )}

          {modalType === 'evaluate' && (
            <>
              <Form.Item
                name="risk_level"
                label="风险等级"
                rules={[{ required: true, message: '请选择风险等级' }]}
              >
                <Select placeholder="请选择风险等级">
                  <Option value="LOW">低</Option>
                  <Option value="MEDIUM">中</Option>
                  <Option value="HIGH">高</Option>
                </Select>
              </Form.Item>

              <Form.Item
                name="risk_score"
                label="风险分值"
                rules={[{ required: true, message: '请输入风险分值' }]}
              >
                <Input placeholder="请输入风险分值" type="number" />
              </Form.Item>

              <Form.Item
                name="reason"
                label="调整原因"
                rules={[{ required: true, message: '请输入调整原因' }]}
              >
                <TextArea placeholder="请输入调整原因" rows={3} />
              </Form.Item>

              <Form.Item
                name="responsible_person"
                label="责任人"
                rules={[{ required: true, message: '请选择责任人' }]}
              >
                <Select placeholder="请选择责任人">
                  {users.map(user => (
                    <Option key={user.id} value={user.id}>{user.name} ({user.username})</Option>
                  ))}
                </Select>
              </Form.Item>
            </>
          )}

          {modalType === 'treatment' && (
            <>
              <Form.Item label="管控措施（可按条数增加）" required>
                <Form.List name="items">
                  {(fields, { add, remove }) => (
                    <div>
                      {fields.map((field, idx) => (
                        <div
                          key={field.key}
                          style={{
                            border: '1px solid rgba(0,0,0,0.06)',
                            borderRadius: 8,
                            padding: 12,
                            marginBottom: 10
                          }}
                        >
                          <Row gutter={12}>
                            <Col xs={24} md={8}>
                              <Form.Item
                                {...field}
                                name={[field.name, 'name']}
                                label={idx === 0 ? '措施名称' : ''}
                                rules={[{ required: true, whitespace: true, message: '请输入措施名称' }]}
                              >
                                <Input placeholder="请输入措施名称" />
                              </Form.Item>
                            </Col>
                            <Col xs={24} md={6}>
                              <Form.Item
                                {...field}
                                name={[field.name, 'risk_level']}
                                label={idx === 0 ? '适用风险等级' : ''}
                                rules={[{ required: true, message: '请选择适用风险等级' }]}
                              >
                                <Select placeholder="请选择适用风险等级">
                                  <Option value="LOW">低</Option>
                                  <Option value="MEDIUM">中</Option>
                                  <Option value="HIGH">高</Option>
                                </Select>
                              </Form.Item>
                            </Col>
                            <Col xs={24} md={10}>
                              <Form.Item
                                {...field}
                                name={[field.name, 'description']}
                                label={idx === 0 ? '措施描述' : ''}
                                rules={[{ required: true, whitespace: true, message: '请输入措施描述' }]}
                              >
                                <Input placeholder="请输入措施描述" />
                              </Form.Item>
                            </Col>
                          </Row>
                          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            {fields.length > 1 ? (
                              <Button
                                danger
                                size="small"
                                onClick={() => remove(field.name)}
                                icon={<MinusCircleOutlined />}
                              >
                                删除该条
                              </Button>
                            ) : null}
                          </div>
                        </div>
                      ))}
                      <Button type="dashed" onClick={() => add({ name: '', risk_level: 'LOW', description: '' })} block icon={<PlusOutlined />}>
                        增加一条
                      </Button>
                    </div>
                  )}
                </Form.List>
              </Form.Item>
            </>
          )}

          {modalType === 'editTreatmentMeasure' && (
            <>
              <Form.Item
                name="name"
                label="措施名称"
                rules={[{ required: true, message: '请输入措施名称' }]}
              >
                <Input placeholder="请输入措施名称" />
              </Form.Item>

              <Form.Item
                name="risk_level"
                label="适用风险等级"
                rules={[{ required: true, message: '请选择适用风险等级' }]}
              >
                <Select placeholder="请选择适用风险等级">
                  <Option value="LOW">低</Option>
                  <Option value="MEDIUM">中</Option>
                  <Option value="HIGH">高</Option>
                </Select>
              </Form.Item>

              <Form.Item
                name="description"
                label="措施描述"
                rules={[{ required: true, message: '请输入措施描述' }]}
              >
                <TextArea placeholder="请输入措施描述" rows={3} />
              </Form.Item>
            </>
          )}

          <Form.Item>
            <Space direction={isMobile ? 'vertical' : 'horizontal'} style={{ display: 'flex', justifyContent: 'flex-end', width: isMobile ? '100%' : 'auto' }}>
              <Button onClick={handleCancel} block={isMobile}>取消</Button>
              <Button type="primary" htmlType="submit" icon={<SaveOutlined />} block={isMobile}>保存</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 风险记录照片预览 */}
      <Modal
        open={riskRecordImageModalVisible}
        onCancel={() => setRiskRecordImageModalVisible(false)}
        footer={null}
        width={isMobile ? '100%' : 800}
        style={isMobile ? { top: 16 } : undefined}
        zIndex={2000}
      >
        <img
          src={riskRecordPreviewImage}
          alt="预览"
          style={{ width: '100%', height: 'auto' }}
        />
      </Modal>

      <Modal
        title="隐患风险记录"
        open={riskRecordDetailVisible}
        onCancel={closeRiskRecordDetail}
        footer={
          <Space direction={isMobile ? 'vertical' : 'horizontal'} wrap style={isMobile ? { width: '100%' } : undefined}>
            <Button onClick={closeRiskRecordDetail} block={isMobile}>关闭</Button>
            <Button
              icon={<EditOutlined />}
              onClick={() => {
                if (riskRecordDetail) showModal('editHazardRecord', riskRecordDetail);
                closeRiskRecordDetail();
              }}
              block={isMobile}
            >
              编辑
            </Button>
            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={() => {
                if (!riskRecordDetail) return;
                handleDeleteHazardRecord(riskRecordDetail);
                closeRiskRecordDetail();
              }}
              block={isMobile}
            >
              删除
            </Button>
          </Space>
        }
        width={isMobile ? '100%' : 720}
        style={isMobile ? { top: 16 } : undefined}
        destroyOnClose
      >
        {riskRecordDetail ? (
          <div>
            <Row gutter={[16, 16]}>
              <Col xs={24} md={18}>
                <Descriptions column={isMobile ? 1 : 2} size="small" bordered>
                  <Descriptions.Item label="风险所在场所 / 部位" span={2}>
                    {riskRecordDetail?.risk_place_part || '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="风险描述" span={2}>
                    {riskRecordDetail?.risk_description || '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="风险等级">
                    {riskRecordDetail?.risk_level || '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="风险值">
                    {riskRecordDetail?.risk_value ?? '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="可能性等级">
                    {riskRecordDetail?.likelihood_level ?? '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="损失等级">
                    {riskRecordDetail?.loss_level ?? '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="检查周期">
                    {riskRecordDetail?.inspection_cycle || '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="责任人">
                    {riskRecordDetail?.inspection_responsible || '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="创建时间" span={2}>
                            {getRiskRecordCreatedAtText(riskRecordDetail)}
                          </Descriptions.Item>
                  <Descriptions.Item label="可能后果" span={2}>
                    {riskRecordDetail?.possible_consequence || '-'}
                  </Descriptions.Item>
                </Descriptions>
              </Col>
              <Col xs={24} md={6}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ marginBottom: 8, color: 'rgba(0,0,0,0.65)' }}>二维码</div>
                  <QRCode value={buildRiskRecordQrValue(riskRecordDetail)} size={160} />
                </div>
              </Col>
            </Row>
          </div>
        ) : null}
      </Modal>
    </div>
  );
};

export default RiskClassificationPage;